"use client";

import { Suspense, useState, useEffect } from "react";
import { useSearchParams, useRouter } from "next/navigation";
import {
  getPodcasts,
  getYouTubeVideos,
  getAppInfo,
  getPodcastCategories,
} from "lib/getFetchers";
import { listToCategoryTree, flattenCategoryTree } from "lib/categoryTree";
import Breadcrumb from "components/Breadcrumb";
import PodcastCard from "components/PodcastCard";
import YouTubeCard from "components/YouTubeCard";
import AppSection from "components/AppSection";
import TaxonomyCategoryFilter from "components/TaxonomyCategoryFilter";
import CategoryListSkeleton from "components/CategoryListSkeleton";
import ux from "styles/pages/news-podcast-list.module.css";

function PodcastPageContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const categoryId = (searchParams.get("categories") || "").trim();

  const [podcasts, setPodcasts] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [podcastsListLoading, setPodcastsListLoading] = useState(true);
  const [podcastsLoadMore, setPodcastsLoadMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [appInfo, setAppInfo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [videosPagination, setVideosPagination] = useState(null);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videosCurrentPage, setVideosCurrentPage] = useState(1);
  const [categoryOptions, setCategoryOptions] = useState([]);

  const podcastQuery = (page) => {
    let q = `limit=20&sort=-createdAt&page=${page}`;
    if (categoryId) q += `&categories=${encodeURIComponent(categoryId)}`;
    return q;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [
          { appInfo: appInfoData },
          { videos: videosData, pagination: videosPaginationData },
        ] = await Promise.all([
          getAppInfo(),
          getYouTubeVideos("limit=6&sort=-createdAt&page=1"),
        ]);
        if (cancelled) return;
        setAppInfo(appInfoData);
        setVideos(videosData);
        setVideosPagination(videosPaginationData);
        setVideosCurrentPage(1);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { categories = [], error } = await getPodcastCategories(
        "limit=500&sort=position",
      );
      if (cancelled || error) return;
      const list = Array.isArray(categories) ? categories : [];
      const tree = listToCategoryTree(list);
      setCategoryOptions(flattenCategoryTree(tree));
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPodcastsListLoading(true);
      setPodcasts([]);
      setPagination(null);
      setCurrentPage(1);
      try {
        const {
          podcasts: podcastsData,
          pagination: paginationData,
        } = await getPodcasts(podcastQuery(1));
        if (cancelled) return;
        setPodcasts(podcastsData || []);
        setPagination(paginationData);
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setPodcastsListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [categoryId]);

  const handleCategorySelect = (id) => {
    if (id) {
      router.replace(`/podcast?categories=${encodeURIComponent(id)}`, {
        scroll: false,
      });
    } else {
      router.replace("/podcast", { scroll: false });
    }
  };

  const loadMore = async () => {
    if (podcastsLoadMore || !pagination) return;

    const nextPage = currentPage + 1;
    if (nextPage > pagination.pageCount) return;

    setPodcastsLoadMore(true);
    try {
      const { podcasts: newPodcasts, pagination: newPagination } =
        await getPodcasts(podcastQuery(nextPage));
      setPodcasts((prev) => [...prev, ...(newPodcasts || [])]);
      setPagination(newPagination);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error("Error loading more podcasts:", error);
    } finally {
      setPodcastsLoadMore(false);
    }
  };

  const loadMoreVideos = async () => {
    if (videosLoading || !videosPagination) return;

    const nextPage = videosCurrentPage + 1;
    if (nextPage > videosPagination.pageCount) return;

    setVideosLoading(true);
    try {
      const { videos: newVideos, pagination: newPagination } =
        await getYouTubeVideos(`limit=6&sort=-createdAt&page=${nextPage}`);
      setVideos((prev) => [...prev, ...newVideos]);
      setVideosPagination(newPagination);
      setVideosCurrentPage(nextPage);
    } catch (error) {
      console.error("Error loading more videos:", error);
    } finally {
      setVideosLoading(false);
    }
  };

  const breadcrumbItems = [
    { label: "Нүүр хуудас", url: "/" },
    { label: "Podcast", url: null },
  ];

  const hasMore = pagination && currentPage < pagination.pageCount;
  const hasMoreVideos =
    videosPagination && videosCurrentPage < videosPagination.pageCount;

  const selectedCategoryName = categoryId
    ? categoryOptions.find((c) => String(c._id) === categoryId)?.name ||
      categoryOptions.find((c) => String(c._id) === categoryId)?.title ||
      ""
    : "";

  return (
    <div className="container py-4">
      <Breadcrumb items={breadcrumbItems} />
      <h1
        style={{
          fontSize: "1.4rem",
          marginBottom: "1rem",
          marginTop: "1rem",
          fontWeight: 600,
        }}
      >
        Podcast
      </h1>

      <TaxonomyCategoryFilter
        options={categoryOptions}
        selectedId={categoryId}
        onSelect={handleCategorySelect}
        ariaLabel="Podcast — ангиллаар шүүх"
      />

      {podcastsListLoading ? (
        <div aria-busy="true" aria-live="polite">
          <span className="visually-hidden">Подкастын жагсаалт ачааллаж байна</span>
          <span className={ux.loadingLabel}>Ачааллаж байна…</span>
          <CategoryListSkeleton variant="podcast" />
        </div>
      ) : (
        <div key={categoryId || "all"} className={ux.contentReveal}>
          {podcasts.length === 0 ? (
            <div className={ux.emptyWrap}>
              <p className={ux.emptyTitle}>
                {selectedCategoryName
                  ? `«${selectedCategoryName}» ангилалд подкаст алга`
                  : "Подкаст одоогоор байхгүй байна"}
              </p>
              <p className={ux.emptyHint}>
                Өөр ангилал сонгоод үзэх эсвэл шүүлтийг цэвэрлэнэ үү.
              </p>
              {categoryId ? (
                <button
                  type="button"
                  className={ux.resetBtn}
                  onClick={() => handleCategorySelect("")}
                >
                  Бүх подкаст харах
                </button>
              ) : null}
            </div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gap: "20px",
                  marginBottom: "2rem",
                }}
                className="podcast-grid"
              >
                {podcasts.map((podcast) => (
                  <PodcastCard key={podcast._id} podcast={podcast} />
                ))}
              </div>
              {hasMore ? (
                <div className={ux.loadMoreWrap}>
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={podcastsLoadMore}
                    className={ux.loadMoreBtn}
                  >
                    {podcastsLoadMore ? "Ачааллаж байна…" : "Цааш үзэх"}
                  </button>
                </div>
              ) : null}
            </>
          )}
        </div>
      )}

      <style jsx>{`
        .podcast-grid {
          grid-template-columns: repeat(1, 1fr);
        }
        .youtube-grid {
          grid-template-columns: repeat(1, 1fr);
        }
        @media (min-width: 576px) {
          .podcast-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .youtube-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (min-width: 768px) {
          .podcast-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
        @media (min-width: 992px) {
          .podcast-grid {
            grid-template-columns: repeat(4, 1fr) !important;
          }
          .youtube-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
      `}</style>

      {/* YouTube Section */}
      <div style={{ marginTop: "4rem" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "1.5rem",
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="#FF0000">
            <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
          </svg>
          <h2 style={{ fontSize: "1.4rem", margin: 0, fontWeight: 600 }}>
            Youtube
          </h2>
        </div>

        <div
          style={{
            display: "grid",
            gap: "20px",
            marginBottom: "2rem",
          }}
          className="youtube-grid"
        >
          {videos.map((video) => (
            <YouTubeCard key={video._id} video={video} />
          ))}
        </div>

        {videosLoading && videos.length === 0 ? (
          <div style={{ textAlign: "center", margin: "2rem 0", color: "#6b7280" }}>
            Ачааллаж байна…
          </div>
        ) : null}

        {videos.length === 0 && !videosLoading ? (
          <div style={{ textAlign: "center", margin: "4rem 0", color: "#666" }}>
            Мэдээлэл олдсонгүй
          </div>
        ) : null}

        {hasMoreVideos ? (
          <div className={ux.loadMoreWrap}>
            <button
              type="button"
              onClick={loadMoreVideos}
              disabled={videosLoading}
              className={ux.loadMoreBtn}
            >
              {videosLoading ? "Ачааллаж байна…" : "Цааш үзэх"}
            </button>
          </div>
        ) : null}
      </div>

      {appInfo && <AppSection appInfo={appInfo} />}
    </div>
  );
}

export default function PodcastPage() {
  return (
    <Suspense
      fallback={
        <div className="container py-4" style={{ textAlign: "center" }}>
          Хуудас ачааллаж байна…
        </div>
      }
    >
      <PodcastPageContent />
    </Suspense>
  );
}
